/* eslint-disable react/display-name -- ignore anonymous function for forwarding ref */

/* eslint-disable no-restricted-syntax, no-restricted-imports, import/no-extraneous-dependencies -- File defines overrides */
import React from 'react';
import { GestureResponderEvent } from 'react-native';

import { useHref, useLinkClickHandler } from '@remix-run/react';

import { trackNavigationEvent } from 'shared/helpers/hooks/useTrackPerformance';

import { useToPath } from 'platform/preserveQueryParams';
import { useStalePageCheck } from 'platform/useReloadStalePage';

import type {
  ArticleCardProps,
  ButtonProps,
  IconButtonProps,
  LinkProps,
  TextButtonProps,
} from '../bricks-next/types';
import type { SharedCTAProps } from './types';

type ActionProps =
  | LinkProps
  | TextButtonProps
  | IconButtonProps
  | ButtonProps
  | ArticleCardProps['primaryButtonProps']
  | NonNullable<ArticleCardProps['secondaryButtonProps']>;

/**
 * Function that checks if the href is the same origin as the current page.
 * This is useful for determining if a link should be opened via an anchor tag or a router link.
 */
const isSameOrigin = (href: string): boolean => {
  // Shortcircuit for relative URLs
  if (href.startsWith('/')) return true;

  // Check if code is running in a browser environment
  if (typeof window !== 'undefined') {
    const hrefUrl = new URL(href);
    return hrefUrl.origin === window.location.origin;
  }
  // eslint-disable-next-line no-console -- SSR check
  console.warn('Window is not defined, indicating server-side execution.');
  return false;
};

const isString = (value: unknown): value is string => typeof value === 'string';

export function useInternalCTAProps({
  href: to,
  customDisallowedSearchParams,
  onPress: handlePress,
  replace,
  state,
  target,
}: Pick<
  ActionProps,
  'href' | 'customDisallowedSearchParams' | 'onPress' | 'replace' | 'state' | 'target'
>): { href?: string } & Pick<ActionProps, 'onPress'> {
  const checkStalePageAndAuth = useStalePageCheck();

  const getToPath = useToPath(customDisallowedSearchParams);
  const toPath = to ? getToPath(to) : {};
  const href = useHref(toPath);
  const handleRemixClick = useLinkClickHandler(toPath, {
    replace,
    state,
    target,
  });

  // If `to` is undefined or points to a link external to the app, return passed-in props
  // (Can't put this earlier in the function because hooks can't be called conditionally)
  if (!to || (isString(to) && !isSameOrigin(to))) {
    return { href: to, onPress: handlePress };
  }

  const onPress = (event: GestureResponderEvent) => {
    checkStalePageAndAuth();
    trackNavigationEvent();

    handlePress?.(event);
    if (!event.defaultPrevented) {
      /**
       * Bricks-Next's Link component uses a custom onPress handler to handle navigation in a way that's compatible with both web and native. Remix's Link component uses an onClick handler to handle navigation in a way that's performant and compatible with Remix's routing system. We need to call both handlers to ensure that navigation works correctly in both systems. This is the best way to do that without rewriting the entire navigation system.
       */
      handleRemixClick(event as unknown as React.MouseEvent<HTMLAnchorElement, MouseEvent>);
    }
  };

  return { href, onPress };
}

/**
 * Internal HOC that handles navigation via Bricks-Next's component using Remix's routing system. This component is used to handle navigation in a way that's compatible with both web and native.
 */
export function withInternalCTAHandling<T extends ActionProps>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Setting type as any since the HOC is generic
  WrappedComponent: React.ComponentType<any>,
): React.ForwardRefExoticComponent<
  React.PropsWithoutRef<T> & React.RefAttributes<HTMLAnchorElement | HTMLButtonElement>
> {
  return React.forwardRef<HTMLAnchorElement | HTMLButtonElement, T>(
    (
      {
        href: to,
        customDisallowedSearchParams,
        onPress: handlePress,
        replace,
        state,
        target,
        analyticsName,
        ...rest
      }: T,
      ref,
    ) => {
      if (!to) {
        throw new Error(
          'Remix router navigation components must have a properly formatted `to` prop',
        );
      }

      const { href, onPress } = useInternalCTAProps({
        href: to,
        customDisallowedSearchParams,
        onPress: handlePress,
        replace,
        state,
        target,
      });

      return (
        <WrappedComponent
          {...rest}
          analyticsName={analyticsName}
          href={href}
          onPress={onPress}
          ref={ref as React.Ref<unknown>} // Cast the ref to the correct type
          target={target}
        />
      );
    },
  );
}

/**
 * Determines if a CTA should be opened via an anchor tag or a router link.
 * If the href is not the same origin as the current page, it will open via an anchor tag.
 * Otherwise, it will open via a router link.
 *
 * Both are styled via Bricks-Next
 * @throws Error if href is the same origin as the current page
 */
export function withCTAHandling<T extends SharedCTAProps>(
  /* eslint-disable @typescript-eslint/no-explicit-any -- Setting type as any since the HOC is generic */
  BricksNextComponent: React.ComponentType<any>,
  RemixRouterComponent: React.ComponentType<any>,
  /* eslint-enable @typescript-eslint/no-explicit-any */
): React.FC<T> {
  return ({ href, ...rest }: T) => {
    // If href points to a link external to the app OR only uses onPress, use BricksNextComponent
    if (!href || (typeof href === 'string' && !isSameOrigin(href))) {
      // Validate that href for external links are not of the same origin
      return <BricksNextComponent href={href} {...rest} />;
    }

    // If href points to a link internal to the app, use RemixRouterComponent
    return <RemixRouterComponent href={href} {...rest} />;
  };
}
